import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";

export const useMapInitialization = ({
  mapContainer,
  points,
  zoom,
  mapboxStyles,
  selectedEstablishmentId,
  onMapReady,
}) => {
  const mapInstanceRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const initialZoomRef = useRef(zoom);
  const initialCenterRef = useRef([0, 0]);

  // Función para calcular los límites de todos los puntos
  const calculateBounds = (points) => {
    if (points.length === 0) return null;

    const bounds = new mapboxgl.LngLatBounds();
    points.forEach((point) => {
      bounds.extend([point.longitude, point.latitude]);
    });

    return bounds;
  };

  // Configurar zoom y centro inicial basado en puntos
  const configureInitialView = (map) => {
    if (!selectedEstablishmentId) {
      if (points.length > 1) {
        const bounds = calculateBounds(points);
        if (bounds) {
          map.fitBounds(bounds, {
            padding: { top: 100, bottom: 100, left: 100, right: 100 },
            maxZoom: 12,
          });

          map.once("moveend", () => {
            initialZoomRef.current = map.getZoom();
            initialCenterRef.current = map.getCenter().toArray();
          });
        }
      } else if (points.length === 1) {
        map.flyTo({
          center: [points[0].longitude, points[0].latitude],
          zoom: 10,
        });
      }
    }
  };

  // Agregar controles de navegación al mapa
  const addMapControls = (map) => {
    map.addControl(new mapboxgl.NavigationControl(), "top-left");
    map.addControl(
      new mapboxgl.ScaleControl({ maxWidth: 80, unit: "metric" }),
      "bottom-right"
    );
    map.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
      }),
      "top-left"
    );
  };

  useEffect(() => {
    if (!mapContainer.current) return;

    const initialCenter =
      points.length > 0 ? [points[0].longitude, points[0].latitude] : [0, 0];

    // Inicializar el mapa
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapboxStyles || "mapbox://styles/mapbox/light-v11",
      center: initialCenter,
      zoom: zoom,
    });

    mapInstanceRef.current = map;


    addMapControls(map);

    // Configurar la vista inicial cuando el mapa se carga
    map.on("load", () => {
      configureInitialView(map);
      setIsMapReady(true);
      setMapInstance(map);

      if (onMapReady) {
        onMapReady(map);
      }
    });

    // Cleanup al desmontar
    return () => {
      try {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.remove();
          mapInstanceRef.current = null;
        }
        setMapInstance(null);
        setIsMapReady(false);
      } catch (error) {
      }
    };
  }, [points.length, zoom]);

  return {
    mapInstance,
    isMapReady,
    initialZoomRef,
    initialCenterRef,
  };
};
